import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { type z } from 'zod';

import { cx } from '@/lib/cx';
import { SeletorMunicipio } from '@/ui/components/seletor-municipio';
import { SeletorUf } from '@/ui/components/seletor-uf';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { type Endereco, type EnderecoDoCep, type enderecoSchema } from '@fiscoai/contracts';

import { buscarMunicipios, mensagemDeConsulta, useBuscarCep } from './api-consultas';
import { nulo } from './formulario';

export type EnderecoForm = z.input<typeof enderecoSchema>;
type CampoTexto = 'cep' | 'logradouro' | 'numero' | 'complemento' | 'bairro';

const PREENCHIDOS_PELO_CEP = ['logradouro', 'bairro', 'uf', 'municipio'] as const;
const DURACAO_DESTAQUE_MS = 1500;

/** Campos que acabaram de ser preenchidos por uma consulta acendem por um instante. */
function useDestaque() {
  const [campos, setCampos] = useState<ReadonlySet<string>>(new Set());
  const relogio = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(
    () => () => {
      clearTimeout(relogio.current);
    },
    [],
  );
  return {
    tem: (campo: string) => campos.has(campo),
    acender: (lista: readonly string[]) => {
      setCampos(new Set(lista));
      clearTimeout(relogio.current);
      relogio.current = setTimeout(() => {
        setCampos(new Set());
      }, DURACAO_DESTAQUE_MS);
    },
  };
}

/**
 * Endereço com CEP que preenche rua, bairro, estado e cidade (sem apagar número e complemento)
 * e estado/cidade em cascata: a cidade só busca no servidor depois do estado e muda com ele.
 */
export function CamposEndereco({ form }: { form: UseFormReturn<EnderecoForm, unknown, Endereco> }) {
  const erros = form.formState.errors;
  const cep = useBuscarCep();
  const destaque = useDestaque();
  const uf = form.watch('uf');
  const codigo = form.watch('codigoMunicipio');
  const nome = form.watch('municipio');
  const alterar = { shouldDirty: true, shouldValidate: true } as const;

  const aplicarCep = (e: EnderecoDoCep) => {
    if (e.logradouro) form.setValue('logradouro', e.logradouro, alterar);
    if (e.bairro) form.setValue('bairro', e.bairro, alterar);
    form.setValue('uf', e.uf as Endereco['uf'], alterar);
    form.setValue('codigoMunicipio', e.codigoMunicipio, alterar);
    form.setValue('municipio', e.municipio, alterar);
    destaque.acender(PREENCHIDOS_PELO_CEP);
  };
  const consultarCep = (valor: string) => {
    const digitos = valor.replace(/\D/g, '');
    if (digitos.length === 8) cep.mutate(digitos, { onSuccess: aplicarCep });
  };
  const brilho = (campo: string) =>
    destaque.tem(campo) ? { 'data-preenchido': '', className: 'animate-destaque' } : {};
  const texto = (campo: CampoTexto, rotulo: string, classe: string, opcional = false) => (
    <Campo rotulo={rotulo} erro={erros[campo]?.message} className={classe}>
      {(a) => (
        <Input {...a} {...brilho(campo)} {...form.register(campo, opcional ? nulo : undefined)} />
      )}
    </Campo>
  );
  const registroCep = form.register('cep', {
    onChange: (e: { target: { value: string } }) => {
      consultarCep(e.target.value);
    },
  });

  return (
    <>
      <Campo
        rotulo="CEP"
        className="sm:col-span-2"
        erro={
          erros.cep?.message ?? (cep.isError ? mensagemDeConsulta(cep.error, 'CEP') : undefined)
        }
        ajuda="Os 8 números preenchem o resto"
      >
        {(a) => (
          <div className="flex gap-2">
            <Input {...a} {...registroCep} inputMode="numeric" className="font-mono" />
            <Button
              variante="secundario"
              aria-label="Buscar CEP"
              carregando={cep.isPending}
              onClick={() => {
                consultarCep(form.getValues('cep'));
              }}
            >
              <Search aria-hidden />
            </Button>
          </div>
        )}
      </Campo>
      {texto('logradouro', 'Logradouro', 'sm:col-span-4')}
      {texto('numero', 'Número', 'sm:col-span-2')}
      {texto('complemento', 'Complemento', 'sm:col-span-2', true)}
      {texto('bairro', 'Bairro', 'sm:col-span-2')}
      <Campo rotulo="Estado" erro={erros.uf?.message} className="sm:col-span-2">
        {(a) => (
          <Controller
            control={form.control}
            name="uf"
            render={({ field }) => (
              <SeletorUf
                {...a}
                aria-label="Estado"
                className={cx(destaque.tem('uf') && 'animate-destaque rounded-controle')}
                valor={field.value}
                aoMudar={(sigla) => {
                  field.onChange(sigla);
                  form.setValue('codigoMunicipio', '', alterar);
                  form.setValue('municipio', '', alterar);
                }}
              />
            )}
          />
        )}
      </Campo>
      <Campo
        rotulo="Cidade"
        erro={erros.codigoMunicipio?.message ?? erros.municipio?.message}
        complemento={
          codigo ? (
            <Badge
              tom="neutro"
              className="font-mono"
              title="Código IBGE do município (cMun da NF-e)"
            >
              IBGE {codigo}
            </Badge>
          ) : undefined
        }
        className="sm:col-span-4"
      >
        {(a) => (
          <SeletorMunicipio
            {...a}
            aria-label="Cidade"
            className={cx(destaque.tem('municipio') && 'animate-destaque rounded-controle')}
            uf={uf}
            valor={codigo && nome ? { codigo, nome } : null}
            buscar={buscarMunicipios}
            aoMudar={(municipio) => {
              form.setValue('codigoMunicipio', municipio?.codigo ?? '', alterar);
              form.setValue('municipio', municipio?.nome ?? '', alterar);
            }}
          />
        )}
      </Campo>
    </>
  );
}
